import { useState, type ChangeEvent } from 'react';
import { Tick } from '@/components/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PALETTE } from '@/lib/chart-config';
import { cn } from '@/lib/utils';

interface ColorSwatchPickerProps {
    /** Current color; may be empty, in which case `defaultColor` is shown. */
    value: string;
    /** Swatch shown (and implied) when no explicit color is set. */
    defaultColor: string;
    onChange: (color: string) => void;
    disabled?: boolean;
    ariaLabel: string;
    /**
     * The user's own brand colors (e.g. primary + secondary), shown as the first
     * presets ahead of the shared `PALETTE`. Empty/duplicate entries are dropped.
     */
    brandColors?: string[];
}

/**
 * On-brand color picker: a small swatch button that opens a popover of the
 * user's brand colors + the shared `PALETTE` presets, plus a custom-color escape
 * hatch. Replaces the raw `<input type="color">` so decorations default to
 * consistent brand colors.
 */
export default function ColorSwatchPicker({
    value,
    defaultColor,
    onChange,
    disabled = false,
    ariaLabel,
    brandColors,
}: ColorSwatchPickerProps) {
    const [open, setOpen] = useState(false);
    const current = value || defaultColor;

    // Brand colors first, then the shared palette; de-duped case-insensitively so
    // a brand color that already lives in PALETTE isn't shown twice.
    const seen = new Set<string>();
    const presets = [...(brandColors ?? []), ...PALETTE].filter((color) => {
        const key = color?.toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={disabled}
                    aria-label={ariaLabel}
                    className="size-8 flex-shrink-0 cursor-pointer rounded border border-border disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ backgroundColor: current }}
                />
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-2">
                <div className="grid grid-cols-5 gap-1.5">
                    {presets.map((color) => {
                        const selected = current.toLowerCase() === color.toLowerCase();
                        return (
                            <button
                                key={color}
                                type="button"
                                aria-label={color}
                                onClick={() => {
                                    onChange(color);
                                    setOpen(false);
                                }}
                                className={cn(
                                    'flex size-7 items-center justify-center rounded border',
                                    selected ? 'border-foreground' : 'border-border',
                                )}
                                style={{ backgroundColor: color }}
                            >
                                {selected && (
                                    <Tick className="size-3.5 text-white drop-shadow-[0_0_1px_rgba(0,0,0,0.6)]" />
                                )}
                            </button>
                        );
                    })}
                </div>
                <label className="mt-2 flex items-center gap-2 border-t pt-2 text-xs text-muted-foreground">
                    <input
                        type="color"
                        value={current}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChange(event.target.value)
                        }
                        className="size-6 cursor-pointer rounded border border-border bg-transparent"
                        aria-label="Cor personalizada"
                    />
                    Cor personalizada
                </label>
            </PopoverContent>
        </Popover>
    );
}
